// Back closes what's open (the media viewer, a phone's thread panel) instead of leaving the page: while it's open
// there's a history entry of its own (lightboxLogic.ts viewerState: the router's state plus a mark), so Back (a phone's
// back swipe, the Back button, Android's back key) pops that entry and closes it. Closing it any other way (×, a
// swipe, Escape) takes the entry back off, so the next Back works as usual. Nested ones (the viewer over a thread)
// close one Back at a time, newest first.
//
// Taking an entry off is a history.back() whose popstate lands later. Until it has, a new entry would be pushed under
// it and that popstate would look like the person pressing Back (closing a panel and opening a thread in one tap, as
// a jump from search or pins does). So the Backs this module starts are counted: their popstates are ignored, and a
// new entry waits until they've landed.
import { useEffect, useRef } from "react";
import { isViewerState, viewerState } from "./lightboxLogic.ts";

/** history.back() calls started here whose popstate hasn't arrived yet, oldest first. */
const ownBacks: object[] = [];
/** The popstate event that was one of ours (instances see the same event after this module's listener). */
let ownEvent: Event | null = null;
/** Pushes waiting for our Backs to land. */
const waiting = new Set<() => void>();

if (typeof addEventListener === "function") {
  // Added at import, before any instance's listener, so it sees each popstate first.
  addEventListener("popstate", (e) => {
    if (!ownBacks.length) return;
    ownBacks.shift();
    ownEvent = e;
    // After this event's other listeners have run.
    if (!ownBacks.length) setTimeout(settle, 0);
  });
}

function settle() {
  const run = [...waiting];
  waiting.clear();
  run.forEach((fn) => fn());
}

/** Takes our entry off. If its popstate never comes (the page went elsewhere first), stop waiting after a second. */
function ownBack() {
  const back = {};
  ownBacks.push(back);
  history.back();
  // Gives up on this Back only: a later one keeps its own second.
  setTimeout(() => {
    const at = ownBacks.indexOf(back);
    if (at < 0) return;
    ownBacks.splice(at, 1);
    if (!ownBacks.length) settle();
  }, 1000);
}

/** Runs `fn` once no Back of ours is still on its way. */
function whenSettled(fn: () => void): () => void {
  if (!ownBacks.length) {
    fn();
    return () => {};
  }
  waiting.add(fn);
  return () => void waiting.delete(fn);
}

export function useBackCloses(open: boolean, onClose: () => void) {
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    if (!open) return;
    const mark = Math.random().toString(36).slice(2);
    let pushed = false;
    let popped = false;
    let cancelWait = () => {};
    // A tick later: React's development double mount would otherwise add an entry, take it off (history.back is
    // async) and pop the second one's entry, closing it at once. And only once our own Backs have landed.
    const t = setTimeout(() => {
      cancelWait = whenSettled(() => {
        history.pushState(viewerState(history.state, mark), "");
        pushed = true;
      });
    }, 0);
    const onPop = (e: Event) => {
      if (e === ownEvent || !pushed || popped || isViewerState(history.state, mark)) return;
      popped = true;
      close.current();
    };
    addEventListener("popstate", onPop);
    return () => {
      clearTimeout(t);
      cancelWait();
      removeEventListener("popstate", onPop);
      // Closed some other way: take the entry back off, so Back then leaves as usual.
      if (pushed && !popped && isViewerState(history.state, mark)) ownBack();
    };
  }, [open]);
}
